import { Box, InputBase, Slider } from "@mui/material";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Instance as TinyColor } from 'tinycolor2'
import { colorSelectorSliderSlots } from '../sliderStyles/colorSelectorSliderSlots'
import { getSliderChromeBoxSx, useOpacitySliderStyles } from '../sliderStyles/colorSelectorSlider.styles'

export type OpacitySliderProps = {
    value: TinyColor,
    onOpacityChange: (opacity: number) => void
}

const OpacitySlider: React.FC<OpacitySliderProps> = ({value, onOpacityChange}) => {
    const color = useMemo(() => value.clone().setAlpha(1), [value])
    const { classes } = useOpacitySliderStyles({ color })
    const rafRef = useRef<number | undefined>(undefined)

    const pct = Math.round(value.getAlpha() * 100)
    const [inputVal, setInputVal] = useState<string>('')
    const [editing, setEditing] = useState(false)

    const onChange = useCallback((event: Event, newValue: number | number[]) => {
        cancelAnimationFrame(rafRef.current!)
        rafRef.current = requestAnimationFrame(() => {
            const opacity = Array.isArray(newValue) ? newValue[0] : newValue
            onOpacityChange(opacity / 100)
        })
    }, [onOpacityChange])

    const handleInputFocus = useCallback(() => {
        setEditing(true)
        setInputVal(String(pct))
    }, [pct])

    const commitInput = useCallback((raw: string) => {
        setEditing(false)
        const num = parseInt(raw, 10)
        if (!isNaN(num)) {
            onOpacityChange(Math.min(100, Math.max(0, num)) / 100)
        }
    }, [onOpacityChange])

    const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur()
        } else if (e.key === 'Escape') {
            setEditing(false)
        }
    }, [])

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '9px', py: '4px' }}>
            <Box sx={getSliderChromeBoxSx()}>
                <Slider
                    size="small"
                    className={classes.root}
                    slots={colorSelectorSliderSlots}
                    onChange={onChange}
                    value={pct}
                />
            </Box>
            <InputBase
                value={editing ? inputVal : pct}
                onFocus={handleInputFocus}
                onChange={(e) => setInputVal(e.target.value)}
                onBlur={(e) => commitInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                endAdornment={
                    <Box component="span" sx={{ fontSize: '11px', color: 'text.secondary', pr: '4px', pl: '2px', lineHeight: 1, userSelect: 'none' }}>%</Box>
                }
                inputProps={{ style: { textAlign: 'right', padding: 0, paddingLeft: '4px' } }}
                sx={{
                    width: 46,
                    height: 22,
                    fontSize: '12px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '4px',
                    flexShrink: 0,
                }}
            />
        </Box>
    );
}


export default memo(OpacitySlider)